import backgroundImage from '../../assets/Form/FormBackground.jpg'
import logo from '../../assets/Form/DesktopLogo.png'
import LawyerImage from '../../assets/Form/LawyerImage.png'
import { useScroll } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { MoveLeft } from 'lucide-react'
import loader from '../../assets/Form/loader.svg'
import EligibleTick from '../../assets/Form/tick.svg'
import inEligible from '../../assets/Form/NotEligible.svg'
import thankYou from '../../assets/Form/ThankYou.svg'
import { HerniaMeshSendAdminEmail, HerniaMeshSendUserEmail,} from '../../emailService'
import Disclaimer from '../Disclaimer'
import PrivacyPolicy from '../PrivacyPolicy'
import {
    buildAdvancedMatching,
    ensureMetaPixel,
    normalizeEmail,
    normalizePhone,
    isValidEmail,
    isValidPhoneDigits,
    trackEventWithUserData
} from '../../utils/metaPixel'
import { LeadVertical } from '../../utils/common'

//Read url params
const getAdTrackingParams = () => {
    if (typeof window === 'undefined') return {};

    const params = new URLSearchParams(window.location.search);

    return {
        gclid: params.get('gclid'),
        gbraid: params.get('gbraid'),
        wbraid: params.get('wbraid'),
    };
};

const question = [
  {
    id: 1,
    question: 'Did you receive a hernia mesh implant as part of a hernia repair surgery?',
    type: 'radio',
    options: ['Yes', 'No'],
    koCondition: 'No' //  Not Eligible
  },
  {
    id: 2,
    question: 'What complication did you experience after your hernia mesh surgery?',
    type: 'radio', // can be switched to dropdown in UI later
    options: [
      'Mesh failure or migration',
      'Chronic pain',
      'Infection',
      'Adhesion or bowel obstruction',
      'Hernia recurrence',
      'Required revision or removal surgery',
      'Other mesh-related complication',
      'None of the above'
    ],
    koCondition: 'None of the above' //  Not Eligible
  },
  {
    id: 3,
    question: 'Did you require revision or removal surgery due to the complication?',
    type: 'radio',
    options: ['Yes', 'No'],
    // Soft qualify → NO knockout here
    koCondition: null
  },
  {
    id: 4,
    question: 'How old were you when the mesh complication occurred?',
    type: 'radio',
    options: ['Under 18', '18–39', '40–59', '60 or older'],
    koCondition: 'Under 18' //  Not Eligible
    // 60+ is soft qualify → allowed
  },
  {
    id: 5,
    question: 'Did the complication or revision surgery occur within the last 10 years?',
    type: 'radio',
    options: ['Yes', 'No'],
    koCondition: 'No' //  Not Eligible
  },
  {
    id: 6,
    question: 'Are you currently represented by an attorney for this matter?',
    type: 'radio',
    options: ['Yes', 'No'],
    koCondition: 'Yes' //  Not Eligible
  },
  {
    id: 7,
    question: 'Do you have access to medical records related to your hernia mesh surgery or complications?',
    type: 'radio',
    options: ['Yes', 'No'],
    koCondition: 'No' //  Not Eligible
  }
]


const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
]

const durationOptions = [
    'Less than 1 year',
    '1–3 years',
    '3–5 years',
    'More than 5 years',
    'I don\'t remember (but used regularly)'
]

/**
 * Hernia Mesh Form 001 Component
 * 
 * META PIXEL ADVANCED MATCHING IMPLEMENTATION:
 * 
 * This form implements Meta Pixel advanced matching to capture and transmit
 * email IDs and phone numbers according to Meta's specifications.
 * 
 * DATA SPECIFICATIONS:
 * - Email: Normalized to lowercase, unhashed (Meta hashes automatically using SHA-256)
 * - Phone: Normalized to digits only with country code (E.164 format, max 15 digits)
 * 
 * IMPLEMENTATION DETAILS:
 * - Pixel is initialized on component mount for PageView tracking
 * - Advanced matching data is passed in event tracking calls (Lead, CompleteRegistration)
 * - User data is validated and normalized before transmission
 * - Data is only sent when valid email and/or phone are available
 * 
 * PRIVACY & COMPLIANCE:
 * - All data handling complies with Meta's data policy requirements
 * - User data is validated before transmission
 * - Implementation follows Meta's advanced matching best practices
 * 
 * Reference: https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching/
 */
const HerniaMeshForm001 = () => {
    // Meta Pixel ID for Hernia Mesh eligibility form (have to change later)
    const META_PIXEL_ID = "2557997017963583"; // old one
    let initialLandingUrl = null;

    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        FirstName: "",
        LastName: "",
        email: "",
        phoneNumber: "",
        state: "",
        legalAgree: false,
        verifyPerson: false,
    });
    const [errors, setErrors] = useState({});
    const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const checkValidations = useRef(false);

    const adTrackingRef = useRef({
    gclid: null,
    gbraid: null,
    wbraid: null,
    });

    const validate = ({ formData }) => {
        const newErrors = {};
        if (!formData.FirstName?.trim()) newErrors.FirstName = "Required";
        else if (!/^[a-zA-Z\s'-]+$/.test(formData.FirstName.trim()))
            newErrors.FirstName = "First name should only contain letters";

        if (!formData.LastName?.trim()) newErrors.LastName = "Required";
        else if (!/^[a-zA-Z\s'-]+$/.test(formData.LastName.trim()))
            newErrors.LastName = "Last name should only contain letters";

        // Email validation: Must be valid format, will be normalized to lowercase for Meta Pixel
        if (!formData.email?.trim()) newErrors.email = "Required";
        else {
            const normalizedEmail = normalizeEmail(formData.email);
            if (!isValidEmail(normalizedEmail))
                newErrors.email = "Invalid email format";
        }

        // Phone validation: Must be valid digits with country code for Meta Pixel
        if (!formData.phoneNumber?.trim()) newErrors.phoneNumber = "Required";
        else {
            const normalizedPhone = normalizePhone(formData.phoneNumber, { defaultCountryCode: "1" });
            if (!isValidPhoneDigits(normalizedPhone))
                newErrors.phoneNumber = "Invalid phone number. Please enter a valid 10-digit US phone number.";
        }

        if (!formData.state?.trim()) newErrors.state = "Required";

        if (!formData.legalAgree) newErrors.legalAgree = "Required";


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const formatPhoneNumber = (value) => {
        // Remove all non-digit characters
        const digitsOnly = value.replace(/\D/g, '');

        // Limit to 10 digits
        const limitedDigits = digitsOnly.slice(0, 10);

        // Format as (XXX) XXX-XXXX
        if (limitedDigits.length === 0) return '';
        if (limitedDigits.length <= 3) return `(${limitedDigits}`;
        if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
        return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    };

    const HandleInputChange = ({ question: questionKey, e }) => {
        let value = e.target.value;

        if (questionKey === "FirstName" || questionKey === "LastName") {
            value = value.replace(/[^a-zA-Z\s'-]/g, '');
        } else if (questionKey === "phoneNumber") {
            value = formatPhoneNumber(value);
        }

        const updatedFormData = { ...formData, [questionKey]: value };
        setFormData(updatedFormData);

        if (checkValidations.current) {
            validate({ formData: updatedFormData });
        }

        if (currentStep > 0 && currentStep <= question.length) {
            const currentQuestion = question[currentStep - 1];

            if (currentQuestion && questionKey === currentQuestion.question) {
                const ko = currentQuestion.koCondition;
                const isKnockout =
                    Array.isArray(ko) ? ko.includes(value) : ko === value;

                if (isKnockout) {
                    setTimeout(() => {
                        setCurrentStep('Not Eligible');
                    }, 400);
                    return;
                }

                // STEP 7 CONDITIONAL FLOW
                if (
                    currentQuestion.question === 'Is the person diagnosed still living?' &&
                    value === 'Yes'
                ) {
                    setTimeout(() => {
                        setCurrentStep(currentStep + 2);
                    }, 400);
                    return;
                }

                setTimeout(() => {
                    setCurrentStep(currentStep + 1);
                }, 400);
            }
        }
    };



    const HandleCheckbox = ({ question, e }) => {
        setFormData({ ...formData, [question]: e.target.checked });
        if (checkValidations.current) {
            validate({ formData: { ...formData, [question]: e.target.checked } })
        }
    };

    const checkEligibility = () => {
        // If user passed all questions (reached step 6), they are eligible
        // Knockout conditions are already handled in HandleInputChange
        return currentStep === question.length + 1;
    }
    const getSourceUrl = () => {
        if (typeof window === "undefined") return "Unknown";

        // If we haven't stored the initial URL yet, store it
        if (!initialLandingUrl) {
            initialLandingUrl = window.location.href;
        }

        return initialLandingUrl;
    };
    const getIPAddress = async () => {
        try {
            const response = await fetch("https://api.ipify.org?format=json");
            const data = await response.json();
            return data.ip;
        } catch (error) {
            console.error("Failed to get IP address:", error);
            return "IP address not available";
        }
    };

    // Map IANA → US timezone bucket (Retell requirement)
    const getUSTimezone = (tz) => {
        if (!tz) return "unknown";
        if (tz.includes("New_York")) return "ET";
        if (tz.includes("Chicago")) return "CT";
        if (tz.includes("Denver")) return "MT";
        if (tz.includes("Los_Angeles")) return "PT";
        if (tz.includes("Phoenix")) return "MT"; // AZ no DST
        if (tz.includes("Anchorage")) return "AK";
        if (tz.includes("Honolulu")) return "HT";
        return "other";
    };


    const saveToCRM = async ({ payload }) => {
        try {
            const ipAddress = await getIPAddress();
            const response = await fetch('https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    countryName: 'USA',
                    brandType: "Internal",
                    brandName: 'Project 6',
                    websiteName: 'Connect With Attorney',
                    vertical: LeadVertical.HERNIA_MESH,
                    formPath: '/hernia-mesh-form001',
                    formname: 'Hernia Mesh Form 001',
                    data: {
                        submissionDate: new Date().toLocaleString(),
                        name: `${payload.FirstName} ${payload.LastName}`,
                        FirstName: payload.FirstName,
                        LastName: payload.LastName,
                        email: payload.email,
                        phoneNumber: payload.phoneNumber,
                        city: payload.city || "",
                        state: payload.state,
                        zipCode: payload.zipCode || "",
                        fullAddress: payload.fullAddress || "",
                        q1: payload['Did you receive a hernia mesh implant as part of a hernia repair surgery?'],
                        q2: payload['What complication did you experience after your hernia mesh surgery?'],
                        q3: payload['Did you require revision or removal surgery due to the complication?'],
                        q4: payload['How old were you when the mesh complication occurred?'],
                        q5: payload['Did the complication or revision surgery occur within the last 10 years?'],
                        q6: payload['Are you currently represented by an attorney for this matter?'],
                        q7: payload['Do you have access to medical records related to your hernia mesh surgery or complications?'],

                        injuryType: "Hernia Mesh Complication",
                        trustedFormCertUrl: payload.certId,
                        trustedFormPingUrl: payload.pingUrl,
                        trustedFormToken: payload.tokenUrl,
                        ip_address: ipAddress,
                        page_source: getSourceUrl(),
                        gclid: payload.gclid,
                        gbraid: payload.gbraid,
                        wbraid: payload.wbraid,
                    }
                })
            });
            return true
        }
        catch (e) {
            console.log(e);
            return false;
        }
    }

    const sendToRetellCRM = async ({ payload }) => {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const submittedAtUTC = new Date().toISOString();
        const usTimezone = getUSTimezone(timezone);

        const retellPayload = {
            countryName: "USA",
            brandName: "Project 6",
            websiteName: "Connect With Attorney",
            formname: "Hernia Mesh Form 001",
            vertical: LeadVertical.HERNIA_MESH,
            data: {
                fullName: `${payload.FirstName} ${payload.LastName}`,
                pageUrl: getSourceUrl(),

                firstName: payload.FirstName,
                lastName: payload.LastName,
                phone: payload.phoneNumber,
                email: payload.email,

                birthState: payload.state,
                birthDate: "", // not collected
                story: "", // not collected

                hasDiagnosis:
                    payload['Did you receive a hernia mesh implant as part of a hernia repair surgery?'],

                exposureTimeframe:
                    payload['What complication did you experience after your hernia mesh surgery?'],

                hasAttorney:
                    payload['Are you currently represented by an attorney for this matter?'],

                // 🕒 Retell-required time metadata
                timezone,
                usTimezone,
                submittedAtUTC,
            }
        };

        const res = await fetch(
            "https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata/retell",
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(retellPayload),
            }
        );

        if (!res.ok) {
            const text = await res.text();
            console.error("Retell CRM failed:", text);
        }
    } catch (err) {
        console.error("Retell CRM error:", err);
    }
};


    const HandleSubmit = async (e) => {
        e.preventDefault()
        if (!validate({ formData: formData })) {
            checkValidations.current = true;
            return;
        }

        setIsSubmitting(true);

        const getTrustedFormData = () => {
            return new Promise((resolve) => {
                // Try multiple ways to get the data
                const certUrl = document.querySelector("[name='xxTrustedFormCertUrl']")?.value ||
                    document.getElementById('xxTrustedFormCertUrl')?.value;

                const tokenUrl = document.querySelector("[name='xxTrustedFormCertToken']")?.value ||
                    document.getElementById('xxTrustedFormCertToken')?.value;

                const pingUrl = document.querySelector("[name='xxTrustedFormPingUrl']")?.value ||
                    document.getElementById('xxTrustedFormPingUrl')?.value;

                // If we don't have the data immediately, wait a bit
                if (!certUrl) {
                    setTimeout(() => {
                        const retryCertUrl = document.querySelector("[name='xxTrustedFormCertUrl']")?.value ||
                            document.getElementById('xxTrustedFormCertUrl')?.value;
                        const retryTokenUrl = document.querySelector("[name='xxTrustedFormCertToken']")?.value ||
                            document.getElementById('xxTrustedFormCertToken')?.value;
                        const retryPingUrl = document.querySelector("[name='xxTrustedFormPingUrl']")?.value ||
                            document.getElementById('xxTrustedFormPingUrl')?.value;

                        resolve({
                            certId: retryCertUrl,
                            tokenUrl: retryTokenUrl,
                            pingUrl: retryPingUrl
                        });
                    }, 500);
                } else {
                    resolve({
                        certId: certUrl,
                        tokenUrl: tokenUrl,
                        pingUrl: pingUrl
                    });
                }
            });
        };

        const trustedFormData = await getTrustedFormData();

        const payload = {
            ...formData,
            ...trustedFormData,
            gclid: adTrackingRef.current.gclid,
            gbraid: adTrackingRef.current.gbraid,
            wbraid: adTrackingRef.current.wbraid,
        };

        // Send to Lead Prosper API (continue even if it fails)
        // try {
        //     await sendToLeadProsper({ formData: payload });
        // } catch (error) {
        //     console.error('Lead Prosper API call failed, continuing with email sends:', error);
        // }

        try {
            const sentToCrm = await saveToCRM({ payload })
            sendToRetellCRM({ payload });
            Promise.all([
            HerniaMeshSendAdminEmail({ formData: payload }),
            HerniaMeshSendUserEmail({ formData: payload })
        ]).catch(err => {
            console.error("Hernia Mesh email sending failed:", err);
        });

            if (sentToCrm) {
                setCurrentStep('thankyou')
                
                // Track Lead event with advanced matching user data
                // Email and phone are normalized and validated according to Meta's requirements:
                // - Email: lowercase, unhashed (Meta will hash automatically)
                // - Phone: digits only, including country code (E.164 format)
                const userData = buildAdvancedMatching({ 
                    email: payload.email, 
                    phoneNumber: payload.phoneNumber 
                });
                trackEventWithUserData('Thank You', userData);
                //Event snippet google tag
                if (window.gtag) {
                window.gtag('event', 'conversion', {
                    send_to: 'AW-17927818616/mGirCOWqsPIbEPia0-RC',
                    value: 1.0,
                    currency: 'USD',
                });
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setIsSubmitting(false);
        }

    }
    useEffect(() => {
        // When user completes all questions (step 6), show eligible screen
        if (currentStep === question.length + 1) {
            setTimeout(() => { setCurrentStep('eligible') }, 1000)
        }
        if (currentStep === 'eligible') {
            setTimeout(() => { setCurrentStep('contactForm') }, 1000)
        }
    }, [currentStep]);

    // Initialize Meta Pixel on component mount
    // Advanced matching will be passed in event tracking calls when user data is available
    useEffect(() => {
        ensureMetaPixel(META_PIXEL_ID);
        if (window.fbq) window.fbq("track", "PageView");
    }, []);

    //Get click params from url on rendre
    useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    adTrackingRef.current = {
        gclid: params.get('gclid'),
        gbraid: params.get('gbraid'),
        wbraid: params.get('wbraid'),
    };
    }, []);

    //Google Tag
    useEffect(() => {
        if (document.getElementById('gtag-hernia')) return;

        const gtagScript = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17927818616';
        gtagScript.id = 'gtag-hernia';

        const inlineScript = document.createElement('script');
        inlineScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'AW-17927818616');
        `;

        document.head.appendChild(gtagScript);
        document.head.appendChild(inlineScript);
        }, []);


    // Note: CompleteRegistration is tracked when user clicks "Start Eligibility Check"
    // Advanced matching user data will be included if email/phone are already available

    const resetForm = () => {
                setFormData({
                    FirstName: "",
                    LastName: "",
                    email: "",
                    phoneNumber: "",
                    state: "",
                    legalAgree: false,
                    verifyPerson: false,
                });
                setErrors({});
                setCurrentStep(0);
    };

    return (
        <div
        className="flex flex-col items-center bg-cover bg-center min-h-screen px-4 sm:px-6 xl:px-0"
        style={{ backgroundImage: `url(${backgroundImage})` }}
        >


            <div className="w-full flex justify-center max-w-md mx-auto mt-6 mb-10">
            <img src={logo} className="w-[70%] max-w-[190px]" />
            </div>




            {currentStep == 0 && <div
            className="
              bg-white flex flex-col items-center
                px-6 py-7
                rounded-lg
                shadow-[0_2px_6px_rgba(0,0,0,0.15)]
                w-full
                max-w-[360px] lg:max-w-[420px] xl:max-w-[480px]
                mx-auto mt-6 lg:mt-10 xl:mt-14
                scale-[0.92] lg:scale-100 xl:scale-[1.05]
                origin-top
                "

            >





                <img src={LawyerImage} className="max-w-full w-32 sm:w-36 md:w-44"/>


                <div className="text-[#162766] font-lato font-semibold text-xl lg:text-[24px] text-center mt-2 leading-[1.15] lg:leading-[1.2]">
                Hernia Mesh Complication <br />Eligibility Check
                </div>



                <div className="text-[#162766] font-lato font-medium text-[15px] sm:text-[14px] text-center mt-1">
                    You may be eligible if you received a hernia mesh implant and later experienced serious complications requiring additional treatment or surgery.
                </div>

                <div
                    className="
                        text-sm sm:text-[16px] text-[#162766] w-full rounded-xl
                        bg-[rgba(22,39,102,0.06)] text-center py-2 mt-2
                    "
                >
                    This screening takes less than 30 seconds.
                </div>

                <button onClick={() => {
                    // Track CompleteRegistration event (user data will be added in useEffect if available)
                    if (window.fbq) {
                        const userData = buildAdvancedMatching({ 
                            email: formData.email, 
                            phoneNumber: formData.phoneNumber 
                        });
                        trackEventWithUserData('CompleteRegistration', userData);
                    }
                    setCurrentStep(currentStep + 1)
                }}
                    className="
                        text-center text-[#162766] text-lg sm:text-[20px] w-full
                        shadow-[0_5px_24px_0_rgba(0,0,0,0.25)]
                        bg-[#F8D216] rounded-lg py-2 mt-3
                    "
                >
                    Start Eligibility Check
                </button>
            </div>}
            {currentStep > 0 && currentStep <= question.length && (
                <div className="w-full px-2 sm:px-0">
                    {/* Back */}
                    <div className="
                    relative w-full
                    max-w-md lg:max-w-lg xl:max-w-xl
                    mx-auto
                    flex items-center
                    text-[#162766] text-lg
                    gap-3 mb-2
                    ">

                        <MoveLeft
                            className="cursor-pointer"
                            onClick={() => setCurrentStep(currentStep - 1)}
                        />
                        <div class="mx-auto text-[rgba(66,66,66,0.90)] font-inter text-[16px] font-semibold leading-[34px] uppercase">
                            {currentStep === 1 && 'Step 1: MESH IMPLANT CONFIRMATION'}
                            {currentStep === 2 && 'Step 2: COMPLICATION TYPE'}
                            {currentStep === 3 && 'Step 3: REVISION / REMOVAL SURGERY'}
                            {currentStep === 4 && 'Step 4: AGE AT COMPLICATION'}
                            {currentStep === 5 && 'Step 5: TIMELINE'}
                            {currentStep === 6 && 'Step 6: REPRESENTATION CHECK'}
                            {currentStep === 7 && 'Step 7: MEDICAL RECORD ACCESS'}

                        </div>
                    </div>

                    <div
                    className="
                        bg-white flex flex-col items-center
                        p-4 sm:p-6
                        rounded-lg
                        shadow-[0_4px_8px_rgba(0,0,0,0.20)]
                        w-full
                        max-w-md lg:max-w-lg xl:max-w-xl
                        mx-auto mb-12 lg:mb-16
                        lg:scale-100 xl:scale-[1.05]
                        origin-top
                    "
                    >





                        {/* Top loading bar */}
                        <div className="w-full relative mb-6 sm:mb-8">
                            <div className="h-[6px] w-full bg-[#E5E7EB] rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#F8D216] transition-all duration-300"
                                    style={{ width: `${((currentStep) / question.length) * 100}%` }}
                                />
                            </div>
                        </div>



                        {/* Title */}
                        <div className="text-[#162766] font-lato font-semibold text-base sm:text-lg md:text-xl text-center px-2">
                            {question[currentStep - 1].question}
                        </div>

                        {/* Options */}
                        <div className="flex flex-col gap-4 sm:gap-5 mt-8 sm:mt-10 w-full">
                            {question[currentStep - 1].options.map((opt, idx) => (
                                <label
                                    key={idx}
                                    className="
                                        flex items-center gap-3 sm:gap-4 bg-[#F8F9FA]
                                        rounded-full py-3 sm:py-4 px-5 sm:px-6 cursor-pointer
                                        hover:bg-[#F0F1F3] transition-colors
                                    "
                                >
                                    <input
                                        id={`q-${currentStep - 1}-check-${idx}`}
                                        type="radio"
                                        name={`q-${currentStep - 1}`}
                                        checked={formData[question[currentStep - 1].question] === opt}
                                        className="h-3 w-3 sm:h-4 sm:w-4"
                                        value={opt}
                                        onClick={(e) => { HandleInputChange({ question: question[currentStep - 1].question, e }) }}
                                    // checked={formData[question[currentStep - 1]] == opt}
                                    />
                                    <span className="text-[#162766] text-sm sm:text-base">
                                        {opt}
                                    </span>
                                </label>
                            ))}
                        </div>


                    </div></div>

            )}
            {currentStep == question.length + 1 && (
                <div className="flex justify-center mt-12 w-full px-4 sm:px-0">
                    <div
                        className="
                            bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)]
                            px-6 py-20
                            w-full mx-auto
                            max-w-[480px] sm:max-w-[560px] lg:max-w-[640px]
                            text-center
                            relative
                        "
                    >
                        {/* Top Gold Accent Bar */}
                        <div className="absolute top-0 left-0 w-full h-[9px] bg-[#F8D216] rounded-t-xl"></div>

                        {/* Circular Loader */}
                        <div className="flex justify-center mb-4">
                            <div className="relative w-28 h-28">
                                <div className="absolute inset-0 border-4 border-[#E6E6E6] rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-[#023437] border-t-transparent rounded-full animate-spin"></div>
                                <img src={loader} alt="" className="absolute inset-0 m-auto w-14 h-14" />
                            </div>
                        </div>

                        {/* Text */}
                        <div className="text-[#023437] font-medium text-3xl break-words">
                            Checking your eligibility. This should take less than a minute.

                        </div>

                    </div>
                </div>
            )}
            {currentStep == "eligible" && (
                <div className="w-full flex justify-center mt-6 px-4 sm:px-6">
                    <div className="
      bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.15)]
      px-5 py-6
      w-full max-w-[650px] mx-auto
      text-center
      relative
      animate-fade-in
    ">
                        <div className="absolute top-0 left-0 w-full h-[10px] bg-[#F8D216] rounded-t-2xl" />

                        <div className="flex justify-center w-full">
                            <img
                                src={EligibleTick}
                                className="w-37 sm:w-47 animate-pop"
                            />
                        </div>

                        <h2 className="text-[#162766] font-semibold text-[28px] mb-3 mt-3">
                            You’re Eligible!
                        </h2>

                        <p className="text-[#162766] text-[18px] leading-relaxed max-w-[420px] mx-auto">
                            You meet the criteria. You’re one step closer
                            to getting the help you deserve.
                        </p>
                    </div>
                </div>
            )}

            


            {currentStep == 'Not Eligible' && (
                <div className="w-full flex flex-col justify-center items-center mt-6 px-4 sm:px-6">

                    <div className="w-full max-w-[650px] flex items-center gap-3 mb-4">
                        <MoveLeft
                            className="text-[#162766] text-xl cursor-pointer"
                            onClick={resetForm}
                        />

                        <span className="text-[#162766] text-sm font-medium tracking-wide">
                            HERNIA MESH LAWSUIT ELIGIBILITY CHECK
                        </span>
                    </div>

                    <div
                        className="
                            bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.15)]
                            px-6 sm:px-10 py-16
                            w-full max-w-[650px] mx-auto
                            text-center
                            relative
                        "
                    >
                        <div className="absolute top-0 left-0 w-full h-[10px] bg-[#F8D216] rounded-t-2xl" />

                        {/* updated image */}
                        <div className="flex justify-center w-full">
                            <img
                                src={inEligible}
                                className="w-24 sm:w-32 animate-pop"
                            />
                        </div>

                        <h2 className="text-[#162766] font-semibold text-[28px] mb-3 mt-2">
                            Not Eligible
                        </h2>

                        <p className="text-[#162766] text-[15px] leading-relaxed max-w-[420px] mx-auto">
                            Thank you for sharing your information. At this time, you do not meet the legal eligibility requirements for an Hernia Mesh Lawsuit claim.
                        </p>
                    </div>
                </div>
            )}



            {currentStep === "contactForm" && (
                <form className="w-full flex justify-center mt-6 px-4 sm:px-6">
                    <input
                        type="hidden"
                        id="xxTrustedFormCertUrl"
                        name="xxTrustedFormCertUrl"
                    //   value={certId}
                    />
                    <input
                        type="hidden"
                        id="xxTrustedFormCertToken"
                        name="xxTrustedFormCertToken"
                    //   value={tokenUrl}
                    />
                    <input
                        type="hidden"
                        id="xxTrustedFormPingUrl"
                        name="xxTrustedFormPingUrl"
                    //   value={pingUrl}
                    />
                    <div
                        className="
                            bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.15)]
                            px-5 py-4
                            w-full
                            max-w-[520px] lg:max-w-[600px] xl:max-w-[680px]
                            mx-auto mt-4 lg:mt-10 xl:mt-14
                            text-center
                            relative
                            scale-[0.9] lg:scale-100 xl:scale-[1.05]
                            origin-top
                            text-sm sm:text-base
                        "
                        >


                        <div className="absolute top-0 left-0 w-full h-[6px] bg-[#F8D216] rounded-t-2xl" />

                        <h2 className="text-[#162766] font-semibold text-xl sm:text-2xl leading-snug mb-4">
                        Please provide your details<br />so we can get to work
                        </h2>


                        <div className="flex flex-col gap-4 text-left">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                    <input
                                        type="text"
                                        placeholder="First Name"
                                        value={formData.FirstName}
                                        onChange={(e) => HandleInputChange({ question: "FirstName", e })}
                                        className={`
                px-4 py-3 w-full rounded-lg focus:outline-none
                border
                text-base sm:text-lg
                placeholder:text-[#162766]/50
                ${errors.FirstName ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
              `}
                                    />
                                    {errors.FirstName && (
                                        <span className="text-[12px] text-red-500 mt-1">{errors.FirstName}</span>
                                    )}
                                </div>

                                <div className="flex flex-col">
                                    <input
                                        type="text"
                                        placeholder="Last Name"
                                        value={formData.LastName}
                                        onChange={(e) => HandleInputChange({ question: "LastName", e })}
                                        className={`
                px-4 py-3 w-full rounded-lg focus:outline-none
                border
                text-base sm:text-lg
                placeholder:text-[#162766]/50
                ${errors.LastName ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
              `}
                                    />
                                    {errors.LastName && (
                                        <span className="text-[12px] text-red-500 mt-1">{errors.LastName}</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <input
                                    type="email"
                                    placeholder="Email Address"
                                    value={formData.email}
                                    onChange={(e) => HandleInputChange({ question: "email", e })}
                                    className={`
              px-4 py-3 w-full rounded-lg focus:outline-none
              border
              text-base sm:text-lg
              placeholder:text-[#162766]/50
              ${errors.email ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
            `}
                                />
                                {errors.email && (
                                    <span className="text-[12px] text-red-500 mt-1">{errors.email}</span>
                                )}
                            </div>

                            <div className="flex flex-col">
                                <input
                                    type="text"
                                    placeholder="Phone Number (XXX) XXX-XXXX"
                                    value={formData.phoneNumber}
                                    onChange={(e) => HandleInputChange({ question: "phoneNumber", e })}
                                    maxLength={14}
                                    className={`
              px-4 py-3 w-full rounded-lg focus:outline-none
              border
              text-base sm:text-lg
              placeholder:text-[#162766]/50
              ${errors.phoneNumber ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
            `}
                                />
                                {errors.phoneNumber && (
                                    <span className="text-[12px] text-red-500 mt-1">{errors.phoneNumber}</span>
                                )}
                            </div>

                            <div className="flex flex-col">
                                <select
                                    value={formData.state}
                                    onChange={(e) => HandleInputChange({ question: "state", e })}
                                    className={`
              px-4 py-3 w-full rounded-lg focus:outline-none
              border
              text-base sm:text-lg
              placeholder:text-[#162766]/50
              ${errors.state ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
            `}
                                >
                                    <option value="">State of Residence</option>
                                    {states.map((state) => (
                                        <option key={state} value={state}>
                                            {state}
                                        </option>
                                    ))}
                                </select>
                                {errors.state && (
                                    <span className="text-[12px] text-red-500 mt-1">{errors.state}</span>
                                )}
                            </div>


                            <div className="flex flex-col gap-1">
                                <label className="flex items-start gap-3 mt-1 text-[#162766] leading-snug text-[12px] sm:text-base">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={formData.legalAgree}
                                        onChange={(e) => HandleCheckbox({ question: "legalAgree", e })}
                                    />
                                    <span>
                                    I agree to the{' '}
                                    <a href="/PrivacyPolicy" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-200">
                                        Privacy Policy
                                    </a>{' '}
                                    and{' '}
                                    <a href="/Disclaimer" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-200">
                                        Disclaimer
                                    </a>{' '}
                                    and give my express written consent, affiliates and/or lawyer to contact me at the number provided above, even if this number is a wireless number or if I am presently listed on a Do Not Call list. I understand that I may be contacted by telephone, email, text message or mail regarding case options and that I may be called using automatic dialing equipment. Message and data rates may apply. My consent does not require purchase. This is Legal advertising.
                                    </span>

                                </label>
                                {errors.legalAgree && (
                                    <span className="text-[12px] text-red-500 pl-7 -mt-1">
                                        {errors.legalAgree}
                                    </span>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={HandleSubmit}
                            disabled={isSubmitting}
                            className={`
          w-full rounded-lg py-3 mt-8 font-medium
          shadow-[0_5px_24px_rgba(0,0,0,0.25)]
          text-lg sm:text-xl
          bg-[#F8D216] text-[#162766]
          flex items-center justify-center gap-2
          ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}
        `}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-[#162766] border-t-transparent rounded-full animate-spin"></div>
                                    <span>Submitting...</span>
                                </>
                            ) : (
                                'Submit'
                            )}
                        </button>
                    </div>
                </form>
            )}

            {/* Privacy Policy Modal */}
            {showPrivacyPolicy && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-[#162766]">Privacy Policy</h3>
                            <button
                                onClick={() => setShowPrivacyPolicy(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            <PrivacyPolicy />
                        </div>
                        <div className="flex justify-end p-6 border-t border-gray-200">
                            <button
                                onClick={() => setShowPrivacyPolicy(false)}
                                className="px-6 py-2 bg-[#F8D216] text-[#162766] rounded-lg font-medium hover:bg-[#e6c214]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Disclaimer Modal */}
            {showDisclaimer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-[#162766]">Disclaimer</h3>
                            <button
                                onClick={() => setShowDisclaimer(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            <Disclaimer />
                        </div>
                        <div className="flex justify-end p-6 border-t border-gray-200">
                            <button
                                onClick={() => setShowDisclaimer(false)}
                                className="px-6 py-2 bg-[#F8D216] text-[#162766] rounded-lg font-medium hover:bg-[#e6c214]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {
                currentStep === "thankyou" && (
                    <
                        >
                        <img
                            src={thankYou}
                            className="w-120 mx-auto animate-pop"
                        />
                    </>
                )
            }





        </div >

    )
}

export default HerniaMeshForm001;
